import { PageWrapper, OvFormProvider, OvFormContext } from '@components'
import { INVOICE_DEFAULT_VALUES, LABELS, TOOLTIPS, URL_PATHS, BILLING_CONSTANTS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Save } from '@mui/icons-material'
import { Box, Button, Tooltip, Alert, AlertTitle, Typography, Stack } from '@mui/material'
import { ISingleDocument } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { documentValidationSchema } from 'src/partials/forms/invoice/validation/document-line.form.validation'
import { ReadOnlyBanner } from 'src/components/features/billing'

import { UpdateInvoiceForm } from 'src/partials/index.partials'

const InvoiceCreateContent = observer(({ form }: { form: UseFormReturn<any> }) => {
  const { ui, invoice, shared, client, company, billing, auth } = useStore()
  const { setAllowNavigation, allowNavigation, allowNavigationRef } = useContext(OvFormContext)
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const pendingNavigationRef = useRef<string | null>(null)
  const navigate = useNavigate()
  const [isResending, setIsResending] = useState(false)

  const handleBack = useCallback(() => {
    // Let the navigation blocker handle the confirmation
    navigate(URL_PATHS.INVOICE_LIST)
  }, [navigate])

  const handleSuccess = useCallback(
    (data: Partial<ISingleDocument>) => {
      const route = generatePath(URL_PATHS.INVOICE_DETAIL, { id: data.id })
      ui.handleToaster(t('success:invoice_created'), { type: 'success' })

      // Reset form with new document data to clear isDirty state
      form.reset(data)

      // Set ref DIRECTLY and IMMEDIATELY
      if (allowNavigationRef) {
        allowNavigationRef.current = true
      }

      // Store the route and trigger navigation
      pendingNavigationRef.current = route
      setAllowNavigation?.(true)
    },
    [setAllowNavigation, ui, allowNavigationRef, form]
  )

  // Navigate when allowNavigation becomes true
  useEffect(() => {
    if (allowNavigation && pendingNavigationRef.current) {
      const route = pendingNavigationRef.current
      pendingNavigationRef.current = null
      navigate(route)
    }
  }, [allowNavigation, navigate])

  const handleErrors = (errors: Record<string, string[]>, message?: string) => {
    if (!errors) return

    form.clearErrors()
    const errorKeys = Object.keys(errors)

    errorKeys.forEach(singleKey => {
      // Handle nested errors like "lines.0.label" without type casting
      const errorMessages = errors[singleKey]
      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        form.setError(singleKey as any, {
          type: 'manual',
          message: errorMessages[0],
        })
      }
    })

    if (message) toast(message, { type: 'error' })
  }

  const onSubmitCallback = async (values: FieldValues) => {
    try {
      const res = await invoice.create(values)
      handleSuccess(res)
    } catch (e: any) {
      handleErrors(e.errors, e.message)
    }
  }

  const handleSubmit = () => {
    form.handleSubmit(onSubmitCallback)()
  }

  const handleResendVerification = async () => {
    setIsResending(true)
    try {
      await auth.resendVerificationEmail()
      toast.success(t('success:verification_email_resent'))
    } catch (error: any) {
      toast.error(error.message || t('errors:verification_email_failed'))
    } finally {
      setIsResending(false)
    }
  }

  useEffect(() => {
    invoice.reset(true)
    client.getAll()
    if (!shared.has_vat_codes) shared.getVatCodes()
    // Ensure company data is loaded for VAT code filtering
    if (!company.current_company) company.getMyCompany()
    // Load billing status to check permissions
    if (!billing.status) billing.fetchStatus()
  }, [])

  // If user email is not verified, show verification required banner
  if (billing.status && !billing.emailVerified) {
    return (
      <PageWrapper
        backAction={handleBack}
        pageTitle="Nieuwe Factuur">
        <Stack spacing={3}>
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleResendVerification}
                disabled={isResending}>
                {isResending ? 'Verzenden...' : 'Opnieuw verzenden'}
              </Button>
            }>
            <AlertTitle>E-mailverificatie vereist</AlertTitle>
            <Typography variant="body2" gutterBottom>
              Je moet je e-mailadres verifiëren voordat je documenten kunt maken.
              Controleer je inbox voor de verificatie-e-mail.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Geen e-mail ontvangen? Controleer je spam map of klik op &quot;Opnieuw verzenden&quot;.
            </Typography>
          </Alert>
        </Stack>
      </PageWrapper>
    )
  }

  // If user is in read-only mode, show banner and block creation
  if (billing.isReadOnly) {
    return (
      <PageWrapper
        backAction={handleBack}
        pageTitle="Nieuwe Factuur">
        <Stack spacing={3}>
          <ReadOnlyBanner />
          <Alert severity="warning">
            <AlertTitle>{BILLING_CONSTANTS.MESSAGES.READ_ONLY_TITLE}</AlertTitle>
            <Typography variant="body2">
              {BILLING_CONSTANTS.MESSAGES.READ_ONLY_INFO}
            </Typography>
          </Alert>
        </Stack>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      backAction={handleBack}
      pageTitle="Nieuwe Factuur"
      actions={
        <Box
          display="flex"
          gap={1}
          width={isMobile ? '100%' : 'auto'}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleBack}
            sx={{ flex: isMobile ? 1 : 'unset' }}>
            {LABELS.CANCEL}
          </Button>
          <Tooltip title={TOOLTIPS.SAVE_INVOICE}>
            <Button
              size="small"
              endIcon={<Save />}
              variant="contained"
              onClick={handleSubmit}
              disabled={!billing.canEditDocuments || !form.formState.isValid}
              sx={{ flex: isMobile ? 1 : 'unset' }}>
              {LABELS.INVOICE_SAVE}
            </Button>
          </Tooltip>
        </Box>
      }
      fixedActions={true}>
      <UpdateInvoiceForm documentType="invoice" />
    </PageWrapper>
  )
})

export const InvoiceCreateView = observer(() => {
  const { invoice } = useStore()
  const form = useForm({
    resolver: yupResolver(documentValidationSchema),
    mode: 'all',
    defaultValues: INVOICE_DEFAULT_VALUES,
  })

  // Subscribe to form state to enable dirty tracking and error tracking
  // React-hook-form's formState is lazily evaluated - we need to subscribe to it
  React.useEffect(() => {
    const subscription = form.watch(() => {
      // Access formState to trigger tracking
      form.formState.isDirty
      form.formState.errors
      form.formState.isValid
    })
    return () => subscription.unsubscribe()
  }, [form])

  const handleSubmit = () => {
    // This will be handled by the inner component
  }

  return (
    <OvFormProvider
      loading={invoice.loading}
      form={form}
      onSubmit={handleSubmit}>
      <InvoiceCreateContent form={form} />
    </OvFormProvider>
  )
})
